const chapter = {
  id: 'haloalkanes-haloarenes',
  slug: 'haloalkanes-haloarenes',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Haloalkanes & Haloarenes',
        hindi: 'हेलोऐल्केन और हेलोऐरीन',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 2 प्रश्न',
  },
  syllabusTags: ['Organic Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'classification',
      title: {
        english: 'Classification & Nomenclature',
        hindi: 'वर्गीकरण और नामकरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Based on hybridisation of C–X carbon: **alkyl** ($sp^3$, R–X), **vinyl/allyl** ($sp^2$ adj to $sp^3$), **aryl** (X on $sp^2$ ring).',
        hindi: 'C-X कार्बन के संकरण पर आधारित: **एल्काइल** ($sp^3$, R-X), **vinyl/allyl** ($sp^2$ adj से $sp^3$), **एरिल** (X $sp^2$ रिंग पर)।',
            },
            {
              english: 'By number of X: mono-, di-, tri-, polyhalo. Geminal (gem) = both on same C; vicinal (vic) = on adjacent C.',
        hindi: 'X की संख्या के अनुसार: मोनो-, di-, ट्राई-, पॉलीहेलो। जेमिनल (रत्न) = दोनों एक ही सी पर; विसिनल (विक) = आसन्न सी पर।',
            },
            {
              english: 'IUPAC: numbering chain to give halogen lowest locant (when tied with substituent of same priority, alphabetical wins).',
        hindi: 'IUPAC: हैलोजन न्यूनतम लोकेंट देने के लिए नंबरिंग श्रृंखला (जब समान प्राथमिकता के प्रतिस्थापन के साथ बांधा जाता है, तो वर्णमाला जीत जाती है)।',
            },
            {
              english: 'C–X bond: polar ($\\delta+$ on C, $\\delta-$ on X). Bond length: C–F < C–Cl < C–Br < C–I; bond strength: C–F > C–Cl > C–Br > C–I.',
        hindi: 'C–X बंधन: ध्रुवीय (C पर $\\delta+$, X पर $\\delta-$)। बॉन्ड की लंबाई: C-F < C-Cl < C-Br < C-I; बंधन शक्ति: C–F > C–Cl > C–Br > C–I.',
            },
            {
              english: 'BP order: RI > RBr > RCl > RF (van der Waals); BP $\\downarrow$ with branching.',
        hindi: 'बीपी ऑर्डर: आरआई > आरबीआर > आरसीएल > आरएफ (वैन डेर वाल्स); ब्रांचिंग के साथ बीपी $\\downarrow$।',
            },
            {
              english: 'All RX are denser than water; insoluble in water, soluble in organic solvents.',
        hindi: 'सभी RX पानी से सघन हैं; पानी में अघुलनशील, कार्बनिक सॉल्वैंट्स में घुलनशील। **अल्कोहल से**: (HCl को 1°/2° के लिए ZnCl₂ की आवश्यकता होती है; सीधे काम करता है)। (R-OH + SOCl₂ → R-Cl + SO₂ + HCl, **Darzen**) के साथ सर्वश्रेष्ठ - गैसें निकल जाती हैं, शुद्ध उत्पाद।',
            },
          ],
        },
      ],
    },
    {
      id: 'preparation',
      title: {
        english: 'Preparation Methods',
        hindi: 'तैयारी की विधियाँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**From alcohols**: $R\\text{-OH} + HX \\to R\\text{-X}$ (HCl needs ZnCl₂ for 1°/2°; HI/HBr work directly). Best with $SOCl_2$ (R-OH + SOCl₂ → R-Cl + SO₂ + HCl, **Darzen**) — gases escape, pure product.',
        hindi: '**अल्कोहल से**: $R\\text{-OH} + HX \\to R\\text{-X}$ (HCl को 1°/2° के लिए ZnCl₂ की आवश्यकता होती है; HI/HBr सीधे काम करता है)। $SOCl_2$ (R-OH + SOCl₂ → R-Cl + SO₂ + HCl, **Darzen**) के साथ सर्वश्रेष्ठ - गैसें निकल जाती हैं, शुद्ध उत्पाद।',
            },
            {
              english: 'Also $PCl_3, PCl_5, PBr_3, P + I_2 \\to PI_3$.',
        hindi: 'साथ ही $PCl_3, PCl_5, PBr_3, P + I_2 \\to PI_3$.',
            },
            {
              english: '**From hydrocarbons**: free-radical halogenation $R\\text{-H} + X_2 \\xrightarrow{h\\nu} R\\text{-X} + HX$ (low selectivity).',
        hindi: '**हाइड्रोकार्बन से**: फ्री-रेडिकल हैलोजनेशन $R\\text{-H} + X_2 \\xrightarrow{h\\nu} R\\text{-X} + HX$ (कम चयनात्मकता)।',
            },
            {
              english: '**From alkenes**: addition of HX (Markovnikov; with peroxide → anti-Markovnikov for HBr only — Kharasch). Halogenation: $X_2$ → vicinal dihalide.',
        hindi: '**एल्केन्स से**: एचएक्स (मार्कोवनिकोव; पेरोक्साइड के साथ → केवल एचबीआर के लिए एंटी-मार्कोवनिकोव - खराश) का जोड़। हैलोजनीकरण: $X_2$ → विसिनल डाइहैलाइड।',
            },
            {
              english: '**Finkelstein**: $R\\text{-Cl} + NaI \\xrightarrow{\\text{acetone}} R\\text{-I} + NaCl$ (NaCl insoluble in acetone).',
        hindi: '**फिंकेलस्टीन**: $R\\text{-Cl} + NaI \\xrightarrow{\\text{acetone}} R\\text{-I} + NaCl$ (NaCl एसीटोन में अघुलनशील)।',
            },
            {
              english: '**Swarts**: $R\\text{-Br} + AgF \\to R\\text{-F} + AgBr$ (also Hg₂F₂, CoF₂).',
        hindi: '**स्वर्ट्स**: $R\\text{-Br} + AgF \\to R\\text{-F} + AgBr$ (Hg₂F₂, CoF₂ भी)।',
            },
            {
              english: '**Aryl halides**: from arene by direct halogenation (Cl₂/Br₂ + Lewis acid like FeCl₃, anhydrous AlCl₃). From aniline via diazotisation: $ArN_2^+ + CuCl/CuBr \\to ArX$ (**Sandmeyer**) or $+I_2/KI \\to ArI$ (no Cu).',
        hindi: '**एरिल हैलाइड्स**: सीधे हैलोजनीकरण द्वारा एरीन से (Cl₂/Br₂ + लुईस एसिड जैसे FeCl₃, निर्जल AlCl₃)। डायज़ोटाइजेशन के माध्यम से एनिलिन से: $ArN_2^+ + CuCl/CuBr \\to ArX$ (**सैंडमेयर**) या $+I_2/KI \\to ArI$ (कोई Cu नहीं)।',
            },
          ],
        },
      ],
    },
    {
      id: 'sn-reactions',
      title: {
        english: 'Nucleophilic Substitution — SN1 vs SN2',
        hindi: 'नाभिकस्नेही प्रतिस्थापन — SN1 बनाम SN2',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Feature', 'SN2', 'SN1'],
          rows: [
            ['Mechanism', '1-step concerted', '2-step (carbocation)'],
            ['Rate law', '$k[RX][Nu]$', '$k[RX]$'],
            ['Order', '2', '1'],
            ['Stereochemistry', '**Inversion** (Walden)', 'Racemisation (mostly)'],
            ['Substrate pref.', 'Methyl > 1° > 2° > 3°', '3° > 2° > 1° > methyl'],
            ['Solvent', 'Polar **aprotic** (DMSO, DMF)', 'Polar **protic** (H₂O, ROH)'],
            ['Nucleophile', 'Strong (CN⁻, OH⁻, OR⁻)', 'Weak (H₂O, ROH)'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: 'Reactivity of RX in SN2: **R-I > R-Br > R-Cl > R-F** (C–X bond strength).',
        hindi: 'एसएन2 में आरएक्स की प्रतिक्रियाशीलता: **आर-आई > आर-बीआर > आर-सीएल > आर-एफ** (सी-एक्स बांड ताकत)।',
            },
            {
              english: 'In SN1, allylic & benzylic halides are very reactive (resonance-stabilised carbocation).',
        hindi: 'एसएन1 में, एलिलिक और बेंज़िलिक हैलाइड बहुत प्रतिक्रियाशील (अनुनाद-स्थिर कार्बोकेशन) हैं।',
            },
            {
              english: 'Aryl & vinyl halides are inert to SN1/SN2 (sp² carbon, partial double-bond character of C–X, repulsion of nucleophile by π cloud).',
        hindi: 'एरिल और विनाइल हैलाइड्स SN1/SN2 (sp² कार्बन, C-X का आंशिक डबल-बॉन्ड कैरेक्टर, π क्लाउड द्वारा न्यूक्लियोफाइल का प्रतिकर्षण) के लिए निष्क्रिय हैं।',
            },
            {
              english: 'Aryl halides with strong **EWG ortho/para** undergo nucleophilic substitution by **addition–elimination** (Meisenheimer complex; e.g. picric acid synthesis from chlorobenzene + 3 NO₂).',
        hindi: 'मजबूत **EWG ortho/para** के साथ एरिल हेलाइड्स **अतिरिक्त-उन्मूलन** द्वारा न्यूक्लियोफिलिक प्रतिस्थापन से गुजरते हैं (मीज़ेनहाइमर कॉम्प्लेक्स; उदाहरण के लिए क्लोरोबेंजीन + 3 NO₂ से पिक्रिक एसिड संश्लेषण)।',
            },
          ],
        },
      ],
    },
    {
      id: 'elimination',
      title: {
        english: 'Elimination Reactions (E1, E2)',
        hindi: 'विलोपन अभिक्रियाएँ (E1, E2)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Dehydrohalogenation** with alc. KOH gives alkene (β-elimination).',
        hindi: '**डीहाइड्रोहैलोजनेशन** एएलसी के साथ। KOH एल्कीन (β-उन्मूलन) देता है।',
            },
            {
              english: '**Saytzeff rule**: more substituted (more stable) alkene predominates.',
        hindi: '**सैटज़ेफ़ नियम**: अधिक प्रतिस्थापित (अधिक स्थिर) एल्कीन प्रबल होता है। **हॉफमैन नियम**: भारी आधार (जैसे टर्ट-ब्यूटॉक्साइड) कम प्रतिस्थापित एल्कीन देता है।',
            },
            {
              english: '**Hofmann rule**: bulky base (e.g. tert-butoxide) gives less substituted alkene.',
        hindi: '**हॉफमैन नियम**: भारी आधार (जैसे टर्ट-ब्यूटॉक्साइड) कम प्रतिस्थापित एल्कीन देता है।',
            },
            {
              english: 'E2 — concerted, anti-periplanar H and X; rate $= k[RX][B^-]$.',
        hindi: 'E2 - ठोस, एंटी-पेरिप्लानर एच और एक्स; दर $= k[RX][B^-]$.',
            },
            {
              english: 'E1 — 2-step (carbocation), rate $= k[RX]$. Competes with SN1.',
        hindi: 'E1 - 2-चरण (कार्बोकेशन), दर $= k[RX]$। SN1 से प्रतिस्पर्धा करता है।',
            },
            {
              english: 'Substitution vs elimination: small basic Nu / cold ⇒ substitution; bulky strong base / heat ⇒ elimination.',
        hindi: 'प्रतिस्थापन बनाम उन्मूलन: छोटा बुनियादी Nu / ठंडा ⇒ प्रतिस्थापन; भारी मजबूत आधार / ताप ⇒ उन्मूलन।',
            },
          ],
        },
      ],
    },
    {
      id: 'special-reactions',
      title: {
        english: 'Other Important Reactions',
        hindi: 'अन्य महत्वपूर्ण अभिक्रियाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Wurtz**: $2R\\text{-X} + 2Na \\xrightarrow{\\text{dry ether}} R\\text{-R} + 2NaX$ (only symmetrical alkanes; aryl halides give biaryl with $Cu$ — Ullmann).',
        hindi: '**वुर्ट्ज़**: $2R\\text{-X} + 2Na \\xrightarrow{\\text{dry ether}} R\\text{-R} + 2NaX$ (केवल सममित अल्केन्स; एरिल हैलाइड्स $Cu$ - उलमैन के साथ बायरिल देते हैं)।',
            },
            {
              english: '**Wurtz-Fittig** (mixed): $R\\text{-X} + Ar\\text{-X} + 2Na \\to Ar\\text{-R}$.',
        hindi: '**वुर्ट्ज़-फ़िटिग** (मिश्रित): $R\\text{-X} + Ar\\text{-X} + 2Na \\to Ar\\text{-R}$।',
            },
            {
              english: '**Fittig**: $2Ar\\text{-X} + 2Na \\to Ar\\text{-Ar}$.',
        hindi: '**फ़िटिग**: $2Ar\\text{-X} + 2Na \\to Ar\\text{-Ar}$।',
            },
            {
              english: '**Reduction**: $R\\text{-X} + Zn/HCl$ or $LiAlH_4$ → R-H.',
        hindi: '**कमी**: $R\\text{-X} + Zn/HCl$ या $LiAlH_4$ → R-H.',
            },
            {
              english: '**Grignard**: $R\\text{-X} + Mg \\xrightarrow{\\text{dry ether}} R\\text{-MgX}$. Reacts with carbonyls / CO₂ / epoxides → versatile synthesis (any moisture destroys it: $R\\text{-MgX} + H_2O \\to R\\text{-H}$).',
        hindi: '**ग्रिगनार्ड**: $R\\text{-X} + Mg \\xrightarrow{\\text{dry ether}} R\\text{-MgX}$. कार्बोनिल्स / CO₂ / एपॉक्साइड्स के साथ प्रतिक्रिया करता है → बहुमुखी संश्लेषण (कोई भी नमी इसे नष्ट कर देती है: $R\\text{-MgX} + H_2O \\to R\\text{-H}$)।',
            },
            {
              english: '**Polyhalogen compounds**:',
        hindi: '**पॉलीहैलोजन यौगिक**:',
            },
            {
              english: '• CHCl₃ — chloroform; **stored in dark brown bottle** (oxidises in light/air to phosgene COCl₂ — small amount of ethanol added as stabiliser).',
        hindi: '• CHCl₃ - क्लोरोफॉर्म; **गहरे भूरे रंग की बोतल में संग्रहित** (light/air में ऑक्सीकृत होकर फॉस्जीन COCl₂ - स्टेबलाइज़र के रूप में जोड़ी गई इथेनॉल की थोड़ी मात्रा)।',
            },
            {
              english: '• CCl₄ — once used as fire extinguisher; banned (forms phosgene at high T; ozone-depleting).',
        hindi: '• CCl₄ - एक बार आग बुझाने वाले यंत्र के रूप में उपयोग किया जाता है; प्रतिबंधित (उच्च टी पर फॉस्जीन बनाता है; ओजोन-क्षयकारी)।',
            },
            {
              english: '• Iodoform CHI₃ — antiseptic; prepared from ethanol/acetone with $I_2$/NaOH (iodoform test).',
        hindi: '• आयोडोफॉर्म CHI₃ — एंटीसेप्टिक; ethanol/acetone से /NaOH (आयोडोफॉर्म परीक्षण) के साथ तैयार किया गया।',
            },
            {
              english: '• Freons (CFCs, e.g. CCl₂F₂) — ozone-depleting (Cl radicals).',
        hindi: '• फ़्रीऑन (CFCs, जैसे CCl₂F₂) - ओजोन-क्षयकारी (Cl रैडिकल)।',
            },
            {
              english: '• DDT — persistent insecticide, banned.',
        hindi: '• डीडीटी - लगातार कीटनाशक, प्रतिबंधित।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'R\\text{-Cl} + NaI \\xrightarrow{\\text{acetone}} R\\text{-I} + NaCl\\downarrow',
      desc: {
        english: 'Finkelstein',
        hindi: 'फिंकेलस्टीन',
      },
    },
    {
      latex: 'R\\text{-Br} + AgF \\to R\\text{-F} + AgBr',
      desc: {
        english: 'Swarts',
        hindi: 'स्वार्ट्स',
      },
    },
    {
      latex: '2RX + 2Na \\xrightarrow{\\text{ether}} R\\text{-}R + 2NaX',
      desc: {
        english: 'Wurtz',
        hindi: 'वर्ट्ज़',
      },
    },
    {
      latex: 'R\\text{-X} + Mg \\xrightarrow{\\text{ether}} R\\text{-MgX}',
      desc: {
        english: 'Grignard',
        hindi: 'ग्रिग्नार्ड',
      },
    },
    {
      latex: 'R\\text{-OH} + SOCl_2 \\to R\\text{-Cl} + SO_2 + HCl',
      desc: {
        english: 'Darzen',
        hindi: 'डार्ज़ेन',
      },
    },
    {
      latex: 'ArN_2^+ + CuCl \\to ArCl + N_2',
      desc: {
        english: 'Sandmeyer',
        hindi: 'सैंडमेयर',
      },
    },
    {
      latex: 'CHCl_3 + \\tfrac{1}{2}O_2 \\xrightarrow{h\\nu} COCl_2 + HCl',
      desc: {
        english: 'Phosgene formation',
        hindi: 'फॉस्जीन का निर्माण',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Mechanism',
      problem: {
        english: 'Which substrate reacts fastest by SN1?',
        hindi: 'कौन सा सब्सट्रेट SN1 द्वारा सबसे तेज़ प्रतिक्रिया करता है?',
      },
      answer: {
        english: '$(CH_3)_3C\\text{-Br}$',
        hindi: '$(CH_3)_3C\\text{-Br}$',
      },
      solution: {
        english: 'Tertiary carbocation most stable.',
        hindi: 'तृतीयक कार्बोकेशन सबसे स्थिर।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'SN2',
      problem: {
        english: 'In SN2 reaction, configuration:',
        hindi: 'SN2 प्रतिक्रिया में, विन्यास:',
      },
      answer: {
        english: 'Inverted (Walden inversion)',
        hindi: 'उलटा (वाल्डेन उलटा)',
      },
      solution: {
        english: 'Backside attack.',
        hindi: 'बैकसाइड हमला।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Reagent',
      problem: {
        english: 'Best reagent to convert R-OH → R-Cl giving pure product:',
        hindi: 'शुद्ध उत्पाद देने वाले R-OH → R-Cl को परिवर्तित करने के लिए सर्वोत्तम अभिकर्मक:',
      },
      answer: {
        english: 'SOCl₂ (Darzen)',
        hindi: 'SOCl₂ (डार्ज़ेन)',
      },
      solution: {
        english: 'By-products SO₂ & HCl escape as gases.',
        hindi: 'उप-उत्पाद SO₂ और HCl गैसों के रूप में बाहर निकलते हैं।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Aryl',
      problem: {
        english: 'Chlorobenzene is less reactive than CH₃Cl in SN because:',
        hindi: 'एसएन में क्लोरोबेंजीन CH₃Cl की तुलना में कम प्रतिक्रियाशील है क्योंकि:',
      },
      answer: {
        english: 'Partial double-bond character of C–Cl due to resonance',
        hindi: 'अनुनाद के कारण C-Cl का आंशिक दोहरा-बंध चरित्र',
      },
      solution: {
        english: 'Stronger, shorter C–Cl bond.',
        hindi: 'मजबूत, छोटा C-Cl बंधन।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Polyhalogen',
      problem: {
        english: 'Chloroform stored in dark bottle to prevent formation of:',
        hindi: 'क्लोरोफॉर्म को निम्न के निर्माण को रोकने के लिए अंधेरे बोतल में संग्रहित किया जाता है:',
      },
      answer: {
        english: 'Phosgene (COCl₂)',
        hindi: 'फॉस्जीन (COCl₂)',
      },
      solution: {
        english: 'Photo-oxidation by O₂.',
        hindi: 'O₂ द्वारा फोटो-ऑक्सीकरण।',
      },
    },
  ],
  next: {
    slug: 'alcohols-phenols-ethers',
    title: 'Alcohols, Phenols & Ethers',
  },
};

export default chapter;
